import React, { createContext, useContext, useReducer, useCallback } from 'react'
import { TextSelection, AIHighlight, EditorState } from '@types/index'

interface EditorContextType {
  // State
  content: string
  selection: TextSelection | null
  aiHighlights: AIHighlight[]
  cursorPosition: number
  isDirty: boolean
  mode: 'markdown' | 'latex' | 'plain' | 'word' | 'word'
  
  // Actions
  setContent: (content: string) => void
  setSelection: (selection: TextSelection | null) => void
  addAIHighlight: (highlight: AIHighlight) => void
  removeAIHighlight: (id: string) => void
  clearAIHighlights: () => void
  setCursorPosition: (position: number) => void
  setMode: (mode: 'markdown' | 'latex' | 'plain' | 'word') => void
  insertText: (text: string, start?: number, end?: number) => void
  markClean: () => void
  markDirty: () => void
}

const EditorContext = createContext<EditorContextType | undefined>(undefined)

type EditorAction =
  | { type: 'SET_CONTENT'; payload: string }
  | { type: 'SET_SELECTION'; payload: TextSelection | null }
  | { type: 'ADD_AI_HIGHLIGHT'; payload: AIHighlight }
  | { type: 'REMOVE_AI_HIGHLIGHT'; payload: string }
  | { type: 'CLEAR_AI_HIGHLIGHTS' }
  | { type: 'SET_CURSOR_POSITION'; payload: number }
  | { type: 'SET_MODE'; payload: 'markdown' | 'latex' | 'plain' | 'word' }
  | { type: 'INSERT_TEXT'; payload: { text: string; start?: number; end?: number } }
  | { type: 'MARK_CLEAN' }
  | { type: 'MARK_DIRTY' }

interface EditorStateType {
  content: string
  selection: TextSelection | null
  aiHighlights: AIHighlight[]
  cursorPosition: number
  isDirty: boolean
  mode: 'markdown' | 'latex' | 'plain' | 'word'
}

const initialState: EditorStateType = {
  content: '',
  selection: null,
  aiHighlights: [],
  cursorPosition: 0,
  isDirty: false,
  mode: 'markdown',
}

function editorReducer(state: EditorStateType, action: EditorAction): EditorStateType {
  switch (action.type) {
    case 'SET_CONTENT':
      return {
        ...state,
        content: action.payload,
        isDirty: action.payload !== state.content,
      }
    
    case 'SET_SELECTION':
      return {
        ...state,
        selection: action.payload,
      }
    
    case 'ADD_AI_HIGHLIGHT':
      return {
        ...state,
        aiHighlights: [...state.aiHighlights, action.payload],
      }
    
    case 'REMOVE_AI_HIGHLIGHT':
      return {
        ...state,
        aiHighlights: state.aiHighlights.filter(h => h.id !== action.payload),
      }
    
    case 'CLEAR_AI_HIGHLIGHTS':
      return {
        ...state,
        aiHighlights: [],
      }
    
    case 'SET_CURSOR_POSITION':
      return {
        ...state,
        cursorPosition: action.payload,
      }
    
    case 'SET_MODE':
      return {
        ...state,
        mode: action.payload,
      }
    
    case 'INSERT_TEXT': {
      const { text, start, end } = action.payload
      let newContent = state.content
      
      if (start !== undefined && end !== undefined) {
        // Replace text at specific position
        newContent = state.content.slice(0, start) + text + state.content.slice(end)
      } else if (state.selection) {
        // Replace selected text
        newContent = state.content.slice(0, state.selection.start) + text + state.content.slice(state.selection.end)
      } else {
        // Insert at cursor position
        newContent = state.content.slice(0, state.cursorPosition) + text + state.content.slice(state.cursorPosition)
      }
      
      return {
        ...state,
        content: newContent,
        isDirty: true,
        selection: null, // Clear selection after insertion
      }
    }
    
    case 'MARK_CLEAN':
      return {
        ...state,
        isDirty: false,
      }
    
    case 'MARK_DIRTY':
      return {
        ...state,
        isDirty: true,
      }
    
    default:
      return state
  }
}

export const EditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(editorReducer, initialState)

  const setContent = useCallback((content: string) => {
    dispatch({ type: 'SET_CONTENT', payload: content })
  }, [])

  const setSelection = useCallback((selection: TextSelection | null) => {
    dispatch({ type: 'SET_SELECTION', payload: selection })
  }, [])

  const addAIHighlight = useCallback((highlight: AIHighlight) => {
    dispatch({ type: 'ADD_AI_HIGHLIGHT', payload: highlight })
  }, [])

  const removeAIHighlight = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_AI_HIGHLIGHT', payload: id })
  }, [])

  const clearAIHighlights = useCallback(() => {
    dispatch({ type: 'CLEAR_AI_HIGHLIGHTS' })
  }, [])

  const setCursorPosition = useCallback((position: number) => {
    dispatch({ type: 'SET_CURSOR_POSITION', payload: position })
  }, [])

  const setMode = useCallback((mode: 'markdown' | 'latex' | 'plain' | 'word') => {
    dispatch({ type: 'SET_MODE', payload: mode })
  }, [])

  const insertText = useCallback((text: string, start?: number, end?: number) => {
    dispatch({ type: 'INSERT_TEXT', payload: { text, start, end } })
  }, [])

  const markClean = useCallback(() => {
    dispatch({ type: 'MARK_CLEAN' })
  }, [])

  const markDirty = useCallback(() => {
    dispatch({ type: 'MARK_DIRTY' })
  }, [])

  const value: EditorContextType = {
    // State
    content: state.content,
    selection: state.selection,
    aiHighlights: state.aiHighlights,
    cursorPosition: state.cursorPosition,
    isDirty: state.isDirty,
    mode: state.mode,
    
    // Actions
    setContent,
    setSelection,
    addAIHighlight,
    removeAIHighlight,
    clearAIHighlights,
    setCursorPosition,
    setMode,
    insertText,
    markClean,
    markDirty,
  }

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  )
}

export const useEditorStore = () => {
  const context = useContext(EditorContext)
  if (context === undefined) {
    throw new Error('useEditorStore must be used within an EditorProvider')
  }
  return context
}